import { AUDIT_CHECKBOX_COLUMNS, makeAuditCheckboxSettings } from "./audit-checkbox-settings"
import { AuditCheckboxColumn, AuditCheckboxSettings, TreeGridApprovedHourDateGroup } from "./types"

export const TOTAL_APPROVED_HOURS_ROW_ID = "totalApprovedHoursRow"

type TotalHoursRowStatus = {
  readyForCheck: 0 | 1
  readyForCheckEnabled?: boolean
  readyForApproval: 0 | 1
  readyForApprovalEnabled?: boolean
  approved: 0 | 1
  approvedEnabled?: boolean
}

type TotalHoursAuditCheckboxSettingsOptions = TotalHoursRowStatus & {
  isMemberContext: boolean
}

type RowLike = {
  readyForCheck: 0 | 1 | ""
  readyForApproval: 0 | 1 | ""
  approved: 0 | 1 | ""
  readyForCheckCanEdit?: 0 | 1
  readyForApprovalCanEdit?: 0 | 1
  approvedCanEdit?: 0 | 1
}

export const makeTotalHoursAuditCheckboxSettings = (
  options: TotalHoursAuditCheckboxSettingsOptions
): AuditCheckboxSettings => {
  return makeAuditCheckboxSettings({
    ...options,
    readyForCheckEnabled: options.readyForCheck !== 1 && (options.readyForCheckEnabled ?? true),
    readyForApprovalEnabled: options.readyForApproval !== 1 && (options.readyForApprovalEnabled ?? true),
    approvedEnabled: options.approved !== 1 && (options.approvedEnabled ?? true),
  })
}

export const makeTotalHoursRow = ({
  isMemberContext,
  label = "Total time",
  name = "Total time row",
}: {
  isMemberContext: boolean
  label?: string
  name?: string
}) => {
  return {
    id: TOTAL_APPROVED_HOURS_ROW_ID,
    Name: name,
    CanEdit: 1,
    actions: "",
    actionsCanEdit: 0,
    dateCanEdit: 0,
    projectOrTask: label,
    projectOrTaskCanEdit: 0,
    projectOrTaskClass: "reported-hours-total-label",
    descriptionCanEdit: 0,
    workerCanEdit: 0,
    startedCanEdit: 0,
    finishedCanEdit: 0,
    hoursCanEdit: 0,
    hoursClass: "reported-hours-total-value",
    hoursFormula: "Grids.FormatTotalApprovedHours(sum('durationInDecimalHours', '4'))",
    ...makeTotalHoursAuditCheckboxSettings({
      isMemberContext,
      readyForCheck: 0,
      readyForApproval: 0,
      approved: 0,
    }),
  }
}

export const refreshTotalHoursRow = (
  gridId: string,
  gridData: TreeGridApprovedHourDateGroup[],
  isMemberContext: boolean
) => {
  const grid = window.Grids[gridId] as TTGrid | undefined
  if (!grid) return

  const totalHoursRow = grid.GetRowById(TOTAL_APPROVED_HOURS_ROW_ID)
  if (!totalHoursRow) return

  const status = calculateTotalHoursRowStatus(gridData)
  const settings = makeTotalHoursAuditCheckboxSettings({
    isMemberContext,
    ...status,
  })

  applyTotalHoursAuditCheckboxSettings(grid, totalHoursRow, settings)
  refreshTotalHoursAuditCells(grid, totalHoursRow)
}

const calculateTotalHoursRowStatus = (dateGroup: TreeGridApprovedHourDateGroup[]): TotalHoursRowStatus => {
  let readyForCheck = 1 as TotalHoursRowStatus["readyForCheck"]
  let readyForCheckEnabled = true
  let readyForApproval = 1 as TotalHoursRowStatus["readyForApproval"]
  let approved = 1 as TotalHoursRowStatus["approved"]
  let readyForApprovalEnabled = true
  let approvedEnabled = true

  const rows: RowLike[] = dateGroup.flatMap((dateGroup) => [dateGroup, ...dateGroup.Items])

  for (const row of rows) {
    if (row.readyForCheck === 0) readyForCheck = 0
    if (row.readyForApproval === 0) readyForApproval = 0
    if (row.approved === 0) approved = 0
    if (row.readyForCheckCanEdit !== 1 && row.readyForCheck !== 1) readyForCheckEnabled = false
    if (row.readyForApprovalCanEdit !== 1) readyForApprovalEnabled = false
    if (row.approvedCanEdit !== 1) approvedEnabled = false
  }

  return {
    readyForCheck,
    readyForApproval,
    approved,
    readyForCheckEnabled,
    readyForApprovalEnabled,
    approvedEnabled,
  }
}

const applyTotalHoursAuditCheckboxSettings = (grid: TTGrid, totalHoursRow: TRow, settings: AuditCheckboxSettings) => {
  const totalHoursAuditRow = totalHoursRow as TRow &
    Record<AuditCheckboxColumn, AuditCheckboxSettings[AuditCheckboxColumn]>

  AUDIT_CHECKBOX_COLUMNS.forEach((column) => {
    const canEdit = settings[`${column}CanEdit`]
    const canFocus = settings[`${column}CanFocus`]
    const type = settings[`${column}Type`]

    grid.SetAttribute(totalHoursRow, column, "CanEdit", canEdit, 0)
    grid.SetAttribute(totalHoursRow, column, "CanFocus", canFocus, 0)
    grid.SetAttribute(totalHoursRow, column, "Type", type, 0)
    totalHoursAuditRow[column] = settings[column]
  })
}

const refreshTotalHoursAuditCells = (grid: TTGrid, totalHoursRow: TRow) => {
  AUDIT_CHECKBOX_COLUMNS.forEach((column) => {
    grid.RefreshCell(totalHoursRow, column)
  })
}
